import { Form, message } from "antd";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  RestaurantRegister,
  VerifyRegisterOtp,
  ResendOtp,
} from "../../ApiService/Apis/Auth/Auth";
import {
  colors,
  MakeError,
  Makelabel,
  setLocalStorageItem,
} from "../../Utils/Index";
import CommonButton from "../../Components/CommonButton";
import CommonInput from "../../Components/CommonInput";

// Steps: "form" -> "otp"
export default function Register() {
  const navigate = useNavigate();
  const [step, setStep] = useState("form");
  const [showLoading, setShowLoading] = useState(false);

  // Carried forward to Step 2
  const [email, setEmail] = useState("");

  // ── Step 1: submit registration details, triggers OTP email ──
  const handleRegister = (val) => {
    setShowLoading(true);
    RestaurantRegister({
      name: val.name,
      email: val.email,
      mobile_number: val.mobile_number,
      password: val.password,
      address: val.address,
    })
      .then((res) => {
        if (res?.status === "success") {
          setEmail(val.email);
          message.success(res.message || "OTP sent successfully to your email!");
          setStep("otp");
        } else {
          message.error(res?.message || "Registration failed");
        }
      })
      .catch((err) => {
        console.log(err);
        message.error(
          err?.response?.data?.message || "Registration failed. Please try again."
        );
      })
      .finally(() => setShowLoading(false));
  };

  // ── Step 2: verify OTP, completes registration + logs the user in ──
  const handleVerifyOtp = (val) => {
    setShowLoading(true);
    VerifyRegisterOtp({ email, otp: val.otp })
      .then((res) => {
        if (res?.status === "success") {
          message.success(res.message || "Registration successful!");
          setLocalStorageItem("access_token", res?.data?.access_token);
          setLocalStorageItem("userDetail", res?.data?.user);
          if (res?.data?.user?.role === "kitchen") {
            navigate("/order-status");
          } else {
            navigate("/dashboard");
          }
        } else {
          message.error(res?.message || "Invalid OTP");
        }
      })
      .catch((err) => {
        console.log(err);
        message.error(
          err?.response?.data?.message || "OTP verification failed. Please try again."
        );
      })
      .finally(() => setShowLoading(false));
  };

  // ── Resend OTP ──
  const handleResendOtp = () => {
    if (!email) return;
    setShowLoading(true);
    ResendOtp({ email })
      .then((res) => {
        if (res?.status === "success") {
          message.success(res.message || "OTP resent successfully!");
        } else {
          message.error(res?.message || "Failed to resend OTP");
        }
      })
      .catch((err) => {
        console.log(err);
        message.error(err?.response?.data?.message || "Failed to resend OTP");
      })
      .finally(() => setShowLoading(false));
  };

  return (
    <div className=" ">
      <div className="flex justify-center signInbg text-black m-auto h-full items-center">
        <div className="bg-[#26272cb3] w-[400px] flex justify-center flex-col px-8 py-6 m-4 items-center">
          <div className="flex justify-center items-center text-dynmaicBg2">
            <p className="text-4xl font-quicksand font-semibold">Restro</p>
          </div>

          {/* ── STEP 1: Registration details ── */}
          {step === "form" && (
            <>
              <p className="text-dynmaicBg2 text-sm mt-3 mb-1 text-center">
                Create your restaurant account
              </p>
              <Form
                onFinish={(val) => handleRegister(val)}
                className="w-full mt-2"
                labelCol={{ span: 24 }}
              >
                <Form.Item
                  rules={[{ required: true, message: MakeError("enter restaurant name") }]}
                  className="w-full m-1"
                  name={"name"}
                  label={Makelabel("Restaurant Name")}
                >
                  <CommonInput placeholder={"Enter restaurant name"} />
                </Form.Item>

                <Form.Item
                  rules={[
                    { required: true, },
                  ]}
                  className="w-full m-1"
                  name={"email"}
                  label={Makelabel("Email")}
                >
                  <CommonInput placeholder={"Enter your email"} />
                </Form.Item>

                <Form.Item
                  rules={[
                    { required: true, message: MakeError("enter mobile number") },
                    { pattern: /^[0-9]{10}$/, message: MakeError("enter a valid 10-digit mobile number") },
                  ]}
                  className="w-full m-1"
                  name={"mobile_number"}
                  label={Makelabel("Mobile Number")}
                >
                  <CommonInput placeholder={"Enter mobile number"} />
                </Form.Item>

                <Form.Item
                  rules={[
                    { required: true, message: MakeError("enter password") },
                    { min: 6, message: MakeError("password must be at least 6 characters") },
                  ]}
                  className="w-full m-1"
                  name={"password"}
                  label={Makelabel("Password")}
                >
                  <CommonInput type="password" placeholder={"Enter password"} />
                </Form.Item>

                <Form.Item
                  rules={[{ required: true, message: MakeError("enter address") }]}
                  className="w-full m-1"
                  name={"address"}
                  label={Makelabel("Address")}
                >
                  <CommonInput placeholder={"Enter your restaurant address"} />
                </Form.Item>

                <Form.Item className="w-full ">
                  <CommonButton
                    loading={showLoading}
                    margintop={"10px"}
                    htmltype={"submit"}
                    color={colors.green}
                    text={"Register"}
                  />
                </Form.Item>
              </Form>
              <p
                onClick={() => navigate("/")}
                className="text-dynmaicBg2 text-xs mt-2 cursor-pointer underline"
              >
                Already have an account? Login
              </p>
            </>
          )}

          {/* ── STEP 2: Verify OTP ── */}
          {step === "otp" && (
            <>
              <p className="text-dynmaicBg2 text-sm mt-3 mb-1 text-center">
                Enter the OTP sent to <span className="font-semibold">{email}</span>
              </p>
              <Form
                onFinish={(val) => handleVerifyOtp(val)}
                className="w-full mt-2"
                labelCol={{ span: 24 }}
              >
                <Form.Item
                  rules={[{ required: true, message: MakeError("enter OTP") }]}
                  className="w-full m-1"
                  name={"otp"}
                  label={Makelabel("OTP")}
                >
                  <CommonInput placeholder={"Enter 6-digit OTP"} />
                </Form.Item>
                <Form.Item className="w-full ">
                  <CommonButton
                    loading={showLoading}
                    margintop={"10px"}
                    htmltype={"submit"}
                    color={colors.green}
                    text={"Verify & Complete Registration"}
                  />
                </Form.Item>
              </Form>
              <p
                onClick={handleResendOtp}
                className="text-dynmaicBg2 text-xs mt-2 cursor-pointer underline"
              >
                Resend OTP
              </p>
              <p
                onClick={() => setStep("form")}
                className="text-dynmaicBg2 text-xs mt-2 cursor-pointer underline"
              >
                Edit details
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}